import { useEffect, useState, useRef } from "react";

// CSS
import "./PrinterComponent.css";

// Components
import InputField from "./inputField";
import Button from "./button";
import PrintPopup from "./printPopup";

// Functions and hooks
import { QRCodeSVG } from "qrcode.react";
import ReactDOMServer from "react-dom/server";
import { useNotification } from "../../Contexts/NotificationContext";
import { usePopup } from "../../Contexts/PopupContext";
import { GetClientData } from "../../BackendAccess/ClientHooks";
import { GetWarehouseData } from "../../BackendAccess/WarehouseHooks";
import { GetCompanyData } from "../../BackendAccess/CompanyHooks";
import { formatCurrency, useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";

const PrinterComponent = ({
  documentData,
  onPrintSuccess,
  onPrintError,
  className = "w-100%",
  title,
  codeValue,
}) => {
  const [printers, setPrinters] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const printFrameRef = useRef(null);

  const [clientData, setClientData] = useState(null);
  const [warehouseData, setWarehouseData] = useState(null);
  const [companyData, setCompanyData] = useState(null);

  const { showNotification } = useNotification();
  const { showPopup, removePopup } = usePopup();

  const { companySettings, refreshCompanySettings } = useSettings();

  // UI translations use default language
  const { t } = useTranslation("printer");
  const { t: tError } = useTranslation("error");

  // Document translations use company language
  const { t: tDoc } = useTranslation("printer", {
    lng: companySettings?.language,
  });

  // Set default title using t function after t is initialized
  const defaultTitle = t("Traffic Note");
  const currentTitle = title || defaultTitle;

  // QR Code generation function
  const generateQRCode = (text, size = 100) => {
    if (!text) return "";

    // Using QR Server API for QR code generation
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
      text
    )}`;
    return qrUrl;
  };

  useEffect(() => {
    if (window.navigator.printing) {
      window.navigator.printing
        .getPrinters()
        .then((availablePrinters) => {
          if (availablePrinters && availablePrinters.length > 0) {
            setPrinters(availablePrinters);
          } else {
          }
        })
        .catch((err) => {});
    } else {
    }
  }, []);

  useEffect(() => {
    if (documentData && documentData.client && documentData.warehouse) {
      const ClientID = getID(documentData.client);
      const WarehouseID = getID(documentData.warehouse);

      getClientData(ClientID);
      getWarehouseData(WarehouseID);
      getCompanyData();
    }
    console.log("Doc data changed ", documentData);
  }, [documentData]);

  const getID = (string) => {
    const m = string.match(/\(([^)]+)\)/);
    const ID = m ? m[1] : null;
    return ID;
  };

  const getClientData = async (id) => {
    if (id === "No Client") {
      setClientData({ Name: "No Client", Address: "/", Phone: "/" });
      return;
    }
    try {
      const [data, error] = await GetClientData(id);
      setClientData(data);
      if (error) {
        showNotification(error.err, "error");
      }
    } catch (err) {
      showNotification(tError("Error while getting client data."), "error");
    } finally {
    }
  };

  const getWarehouseData = async (id) => {
    try {
      const [data, error] = await GetWarehouseData(id);
      setWarehouseData(data);
      if (error) {
        showNotification(error.err, "error");
      }
    } catch (err) {
      showNotification(tError("Error while getting warehouse data."), "error");
    } finally {
    }
  };

  const getCompanyData = async () => {
    try {
      await refreshCompanySettings();
      const [data, error] = await GetCompanyData();
      setCompanyData(data);
      if (error) {
        showNotification(error.err, "error");
      }
    } catch (err) {
      showNotification(tError("Error while getting company data."), "error");
    } finally {
    }
  };

  const openPrintConfirmation = () => {
    const key = Date.now();
    showPopup(
      <PrintPopup
        title={currentTitle}
        printers={printers}
        onPrint={({ selectedPrinter, isTestMode }) => {
          return handlePrint(selectedPrinter, isTestMode);
        }}
        onClose={() => removePopup(key)}
      />,
      key
    );
  };

  const renderDocumentToHTML = (data) => {
    if (!data || !clientData || !warehouseData) return "";
    const {
      noteId,
      date,
      type,
      client,
      warehouse,
      items = [],
      notes = "",
    } = data;

    const totalAmount = items.reduce(
      (sum, item) => sum + (item.Amount || 0),
      0
    );
    const totalPrice = items.reduce(
      (sum, item) => sum + (item.Price * item.Amount || 0),
      0
    );

    let qrCodeHtml = "";
    if (type === "Delivery") {
      const qrData = {
        warehouseId: getID(warehouse),
        clientId: getID(client),
        items: items.map((i) => ({
          id: i.ItemID || i.id,
          amount: i.Amount || 0,
        })),
      };
      const jsonQrData = JSON.stringify(qrData);
      // Cannot directly use QRCodeSVG here as this is string templating.
      // A placeholder or a more complex setup would be needed if SVG is a must.
      // For simplicity, we might need to reconsider if an image can be generated or use a library that provides string output.
      // However, qrcode.react primarily generates SVG components or canvas.
      // Using ReactDOMServer to render the SVG to string for the HTML print output.
      const qrSVGString = ReactDOMServer.renderToStaticMarkup(
        <QRCodeSVG value={jsonQrData} size={128} includeMargin={true} />
      );
      qrCodeHtml = `<div class="qr-code-container" style="margin-top: 20px; text-align: center;">${qrSVGString}</div>`;
    }

    const itemRows = items
      .map(
        (item) => `
      <tr>
        <td>${item.Name || ""}</td>
        <td>${item.Amount || 0}</td>
        <td>${formatCurrency(item.Price || 0, companySettings.currency)}</td>
        <td>${formatCurrency(
          item.Price * item.Amount || 0,
          companySettings.currency
        )}</td>
      </tr>
    `
      )
      .join("");

    const qrCodeHTML = codeValue
      ? `
      <div class="qr-code-section">
        <img src="${generateQRCode(
          codeValue,
          100
        )}" alt="QR Code" class="qr-code" />
      </div>
    `
      : "";

    return `
      <div class="print-document">
        <div class="print-header">
          <div class="header-content">
            <h1>${
              type === "received"
                ? tDoc("Received Goods Note")
                : type === "delivery"
                ? tDoc("Delivery Note")
                : tDoc("Sale")
            }</h1>
            <div class="print-meta">
              <p><strong>${tDoc("Note ID:")}</strong> ${noteId || "/"}</p>
              <p><strong>${tDoc("Date:")}</strong> ${date ? date : "/"}</p>
            </div>
          </div>
          ${qrCodeHTML}
        </div>
        
        <div class="print-parties">
          <div class="party-box">
            <h3>${tDoc("Client")}</h3>
             <p>${tDoc("Name:")} ${clientData?.Name || "/"}</p>
            <p>${tDoc("Address:")} ${clientData?.Address || "/"}</p>
            <p>${tDoc("Phone:")} ${clientData?.Phone || "/"}</p>
          </div>
          
          <div class="party-box">
            <h3>${tDoc("Company")}</h3>
            <p>${tDoc("Name:")} ${companyData?.Name || "/"}</p>
            <p>${tDoc("Address:")} ${companyData?.Address || "/"}</p>
            <p>${tDoc("Phone:")} ${companyData?.Phone || "/"}</p>
            <p>${tDoc("Email:")} ${companyData?.Email || "/"}</p>
          </div>
        </div>
        
        <div class="warehouse-info">
          <h3>${tDoc("Warehouse")}</h3>
          <p>${tDoc("Name:")} ${warehouseData?.Name || "/"}</p>
          <p>${tDoc("Address:")} ${warehouseData?.Address || "/"}</p>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>${tDoc("Item Name")}</th>
              <th>${tDoc("Amount")}</th>
              <th>${tDoc("Unit Price")}</th>
              <th>${tDoc("Total Price")}</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>${tDoc("Total")}</strong></td>
              <td><strong>${totalAmount}</strong></td>
              <td></td>
              <td><strong>${formatCurrency(
                totalPrice,
                companySettings.currency
              )}</strong></td>
            </tr>
          </tfoot>
        </table>
        
        <div class="signatures">
          <div class="signature-box">
            <p>${tDoc("Delivered by: _________________________")}</p>
          </div>
          <div class="signature-box">
            <p>${tDoc("Driver: _________________________")}</p>
          </div>
          <div class="signature-box">
            <p>${tDoc("Received by: _________________________")}</p>
          </div>
        </div>
        
        <div class="notes">
          <p><strong>${tDoc("Notes:")}</strong> ${notes}</p>
        </div>
        ${qrCodeHtml}
      </div>
    `;
  };

  // React component version for preview mode
  const generatePrintContent = () => {
    if (!documentData || !clientData || !warehouseData) return null;
    console.log("doc data", documentData);

    const { noteId, date, type, client, warehouse, items = [] } = documentData;

    const totalAmount = items.reduce(
      (sum, item) => sum + (item.Amount || 0),
      0
    );
    const totalPrice = items.reduce(
      (sum, item) => sum + (item.Price * item.Amount || 0),
      0
    );

    let qrCodeComponent = null;
    if (type) {
      const qrData = {
        warehouseId: getID(documentData.warehouse),
        clientId: getID(documentData.client),
        items: documentData.items.map((i) => ({
          id: i.ItemID || i.id,
          amount: i.Amount || 0,
        })),
      };
      const jsonQrData = JSON.stringify(qrData);
      qrCodeComponent = (
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <QRCodeSVG value={jsonQrData} size={128} />
        </div>
      );
    }

    return (
      <div className="print-document preview">
        <div className="print-header">
          <div className="header-content">
            <h1>
              {type === "received"
                ? tDoc("Received Goods Note")
                : type === "delivery"
                ? tDoc("Delivery Note")
                : tDoc("Sale")}
            </h1>
            <div className="print-meta">
              <p>
                <strong>{tDoc("Note ID:")}</strong> {noteId}
              </p>
              <p>
                <strong>{tDoc("Date & time:")}</strong> {date ? date : ""}
              </p>
            </div>
          </div>
          {codeValue && (
            <div className="qr-code-section">
              <img
                src={generateQRCode(codeValue, 100)}
                alt="QR Code"
                className="qr-code"
              />
            </div>
          )}
        </div>

        <div className="print-parties">
          <div className="party-box">
            <h3>{tDoc("Client")}</h3>
            <p>
              {tDoc("Name:")} {clientData?.Name || "/"}
            </p>
            <p>
              {tDoc("Address:")} {clientData?.Address || "/"}
            </p>
            <p>
              {tDoc("Phone:")} {clientData?.Phone || "/"}
            </p>
          </div>

          <div className="party-box">
            <h3>{tDoc("Company")}</h3>
            <p>
              {tDoc("Name:")} {companyData?.Name || "/"}
            </p>
            <p>
              {tDoc("Address:")} {companyData?.Address || "/"}
            </p>
            <p>
              {tDoc("Phone:")} {companyData?.Phone || "/"}
            </p>
            <p>
              {tDoc("Email:")} {companyData?.Email || "/"}
            </p>
          </div>
        </div>

        <div className="warehouse-info">
          <h3>{tDoc("Warehouse")}</h3>
          <p>
            {tDoc("Name:")} {warehouseData?.Name || "/"}
          </p>
          <p>
            {tDoc("Address:")} {warehouseData?.Address || "/"}
          </p>
        </div>

        <table className="items-table">
          <thead>
            <tr>
              <th>{tDoc("Item Name")}</th>
              <th>{tDoc("Amount")}</th>
              <th>{tDoc("Unit Price")}</th>
              <th>{tDoc("Total Price")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>{item.Name || ""}</td>
                <td>{item.Amount || 0}</td>
                <td>
                  {formatCurrency(item.Price || 0, companySettings.currency)}
                </td>
                <td>
                  {formatCurrency(
                    item.Price * item.Amount || 0,
                    companySettings.currency
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>{tDoc("Total")}</strong>
              </td>
              <td>
                <strong>{totalAmount}</strong>
              </td>
              <td></td>
              <td>
                <strong>
                  {formatCurrency(totalPrice, companySettings.currency)}
                </strong>
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="signatures">
          <div className="signature-box">
            <p>{tDoc("Delivered by: _________________________")}</p>
          </div>
          <div className="signature-box">
            <p>{tDoc("Driver: _________________________")}</p>
          </div>
          <div className="signature-box">
            <p>{tDoc("Received by: _________________________")}</p>
          </div>
        </div>

        <div className="notes">
          <p>
            <strong>{tDoc("Created by:")}</strong>{" "}
            {documentData.createdBy || ""}
          </p>
        </div>

        <div className="notes">
          <p>
            <strong>{tDoc("Notes:")}</strong> {documentData.notes || ""}
          </p>
        </div>
        {qrCodeComponent}
      </div>
    );
  };

  const handlePrint = (selectedPrinter, isTestMode) => {
    setIsPrinting(true);

    if (isTestMode) {
      const key = Date.now();
      showPopup(
        <div className="print-preview-overlay">
          <div className="print-preview-container">
            <h3>{t("Print Preview")}</h3>
            <div className="print-preview-content">
              {generatePrintContent()}
            </div>
            <Button
              btnStyle="btn-secondary"
              btnSize="btn-small"
              onClick={() => {
                removePopup(key);
                setIsPrinting(false);
                if (onPrintSuccess) onPrintSuccess();
              }}
            >
              {t("Close Preview")}
            </Button>
          </div>
        </div>,
        key
      );
    } else {
      try {
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "absolute";
        printFrame.style.top = "-9999px";
        printFrame.style.left = "-9999px";
        printFrame.style.width = "210mm"; // A4 width
        printFrame.style.height = "297mm"; // A4 height
        document.body.appendChild(printFrame);
        printFrameRef.current = printFrame;

        printFrame.onload = () => {
          const doc =
            printFrame.contentDocument || printFrame.contentWindow.document;
          doc.open();
          doc.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${t("Print")} ${currentTitle}</title>
                <style>
                  @page {
                    size: A4;
                    margin: 15mm;
                  }
                  body {
                    font-family: Arial, sans-serif;
                    margin: 0;
                    padding: 0;
                    font-size: 12px;
                  }
                  .print-document {
                    width: 100%;
                  }
                  .print-header {
                    border-bottom: 1px solid #ccc;
                    margin-bottom: 10px;
                    padding-bottom: 10px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                  }
                  .header-content {
                    flex: 1;
                  }
                  .print-header h1 {
                    font-size: 18px;
                    margin: 0;
                  }
                  .print-meta {
                    font-size: 12px;
                  }
                  .qr-code-section {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                  }
                  .qr-code {
                    width: 80px;
                    height: 80px;
                    margin-bottom: 5px;
                  }
                  .qr-code-text {
                    font-size: 10px;
                    margin: 0;
                    word-break: break-all;
                    max-width: 100px;
                  }
                  .print-parties {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 15px;
                  }
                  .party-box {
                    width: 48%;
                    border: 1px solid #ccc;
                    padding: 8px;
                  }
                  .party-box h3 {
                    margin-top: 0;
                    font-size: 14px;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 5px;
                  }
                  .warehouse-info {
                    margin-bottom: 15px;
                    border: 1px solid #ccc;
                    padding: 8px;
                  }
                  .warehouse-info h3 {
                    margin-top: 0;
                    font-size: 14px;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 5px;
                  }
                  .items-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 15px;
                  }
                  .items-table th, .items-table td {
                    border: 1px solid #ccc;
                    padding: 5px;
                    text-align: left;
                  }
                  .items-table th {
                    background-color: #f0f0f0;
                  }
                  .signatures {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 30px;
                  }
                  .signature-box {
                    width: 48%;
                  }
                  .notes {
                    margin-top: 20px;
                    border-top: 1px solid #eee;
                    padding-top: 10px;
                  }
                </style>
              </head>
              <body>
                <div id="printContent"></div>
              </body>
            </html>
          `);
          doc.close();

          const reactRoot = doc.getElementById("printContent");
          if (reactRoot) {
            reactRoot.innerHTML = renderDocumentToHTML(documentData);
          }

          setTimeout(() => {
            try {
              if (window.navigator.printing && selectedPrinter) {
                window.navigator.printing
                  .print({
                    printerName: selectedPrinter,
                  })
                  .then(() => {
                    showNotification(
                      t("Document sent to printer successfully"),
                      "success"
                    );
                    if (onPrintSuccess) onPrintSuccess();
                  })
                  .catch((err) => {
                    console.error("Printing error:", err);
                    showNotification(t("Printing failed"), "error");
                    if (onPrintError) onPrintError(err);
                  })
                  .finally(() => {
                    setIsPrinting(false);
                    cleanupPrintFrame();
                  });
              } else {
                printFrame.contentWindow.focus();
                printFrame.contentWindow.print();

                setTimeout(() => {
                  setIsPrinting(false);
                  cleanupPrintFrame();
                  showNotification(t("Print request completed"), "success");
                  if (onPrintSuccess) onPrintSuccess();
                }, 2000);
              }
            } catch (err) {
              console.error("Print error:", err);
              setIsPrinting(false);
              cleanupPrintFrame();
              showNotification(t("Printing failed"), "error");
              if (onPrintError) onPrintError(err);
            }
          }, 500);
        };
      } catch (err) {
        console.error("Setup error:", err);
        setIsPrinting(false);
        showNotification(t("Failed to prepare document for printing"), "error");
        if (onPrintError) onPrintError(err);
      }
    }
  };

  const cleanupPrintFrame = () => {
    if (printFrameRef.current && printFrameRef.current.parentNode) {
      printFrameRef.current.parentNode.removeChild(printFrameRef.current);
      printFrameRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanupPrintFrame();
    };
  }, []);

  return (
    <Button
      btnStyle="btn-primary"
      onClick={openPrintConfirmation}
      disabled={isPrinting}
      btnMargin={"btn-default"}
    >
      {isPrinting ? t("Printing...") : t("Print Document")}
    </Button>
  );
};

export default PrinterComponent;
